import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { AsyncHorizontalNavQueue } from '@jira/navigation-apps__horizontal-nav-jsm-queue/src/async.tsx';
import { getAIContextPanelResources } from '@jira/router-resources__service-desk-ai-context-panel/src/getAIContextPanelResources.tsx';
import { getResolutionPlanSSRResource } from '@jira/router-resources__service-desk-ai-context-panel/src/ResolutionPlanSSRResource.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import {
	getInsightResources,
	getQueuesResources,
	getTriageButtonPermissionResource,
} from '@jira/router-resources__service-desk-common/src/getServiceDeskSettingsResources.tsx';
import { issueListSsrResource } from '@jira/router-resources__service-desk-queues/src/queuesNavigationItems.tsx';
import { servicedeskIsItsmProjectResource } from '@jira/router-resources__service-desk-is-itsm-project/src/useIsItsmTemplateProject.tsx';
import { customIssueRoute } from '@jira/router-routes__servicedesk-queue-routes/src/customIssueRoute.tsx';
import { serviceProjectLayout } from '@jira/platform__layouts-service-project-layout/src/index.tsx';
import { getDraftReplyPillsResource } from '@jira/router-resources__service-desk-draft-reply/src/getDraftReplyPillsResource.tsx';
import { getJsmIssueViewRelatedArticlesRouteResource } from '@jira/router-resources__jsm-issue-view/src/getJsmIssueViewRelatedArticlesRouteResource.tsx';
import type { Route } from '@atlassian/react-resource-router';
import {
	CustomQueueRouteComponent,
	LazyServicedeskQueuesAgentAndIssueView,
} from './ui/CustomQueueRouteComponent.tsx';
import { QueueLoadingSkeleton } from './ui/queue/index.tsx';

// It is important to keep these in sync:
//   - customQueueRouteEntry
//   - customIssueRouteEntry
//   - csmCustomQueueRouteEntry
//   - csmCustomIssueRouteEntry
// they are rendering the exact same view. This is done to keep the state
// consistent when switching between the two routes.
export const customIssueRouteEntry: Route = createEntry(customIssueRoute, () => ({
	layout: composeLayouts(
		serviceProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{
				params: [
					{
						key: 'issueKey',
					},
				],
			},
		]),
	),

	navigation: {
		menuId: MenuIdProjects,
		// Keep this consistent with the one in customQueueRouteEntry.tsx, so it won't rerender the queue agent view component
		// when navigating between queues and issues.
		// Rereder could cause problem:
		// 1. Issue view will lose issue navigator component
		// 2. When navigating back to queue, the queue will lose the scroll position.

		horizontal: fg('jsm_views_inside_queues_-_main_flag') ? AsyncHorizontalNavQueue : undefined,
	},

	skeleton: fg('jsm_views_inside_queues_-_main_flag') ? QueueLoadingSkeleton : undefined,

	resources: [
		issueListSsrResource,
		...getQueuesResources(),
		...getIssueViewResources(),
		...getInsightResources(),
		...getTriageButtonPermissionResource(),
		getConsolidationStateResource(),
		servicedeskIsItsmProjectResource,
		...getAIContextPanelResources(),
		...getResolutionPlanSSRResource(),
		...getDraftReplyPillsResource(),
		getJsmIssueViewRelatedArticlesRouteResource(),
	],

	afterPaint: [LazyIssueApp],
	group: 'servicedesk',

	// It is important to keep this component consistent across the two routes so
	// that React doesn't unmount it when switching between the routes and the
	// full state is kept so that scroll position and loaded data is kept when
	// going back from issue details to the queues page.
	// eslint-disable-next-line no-restricted-syntax
	component: CustomQueueRouteComponent,

	forPaint: [LazyAtlassianNavigation, LazyServicedeskQueuesAgentAndIssueView],
	supportsPreviewPanel: true,

	bifrost: {
		bifrostGroup: 'shell-only',
	},
}));
